import React, { useState, useCallback, useRef, useEffect } from 'react';

import { Box } from '@mui/joy';

import PiPButtonRight from '../../assets/images/PiPButtonRight.svg?react';
import PiPHide from '../../assets/images/PiPHide.svg?react';
import PipResize from '../../assets/images/PiPResize.svg?react';

interface DualVideoPiPProps {
  position: 'top' | 'bottom';
  borderColor: string;
  children: React.ReactNode;
  onClick?: () => void;
}

const DualVideoPiP: React.FC<DualVideoPiPProps> = ({ position, borderColor, children, onClick }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [videoSize, setVideoSize] = useState({ width: 320, height: 180 });
  const [isResizing, setIsResizing] = useState(false);
  const [isOverResizeHandle, setIsOverResizeHandle] = useState(false);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const [isResizeCooldown, setIsResizeCooldown] = useState(false);
  const resizeCooldownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const toggleVisibility = useCallback(() => {
    setIsVisible((prev) => !prev);
  }, []);

  const handleClick = useCallback((): void => {
    if (isResizing || isOverResizeHandle || isResizeCooldown) return;
    onClick?.();
  }, [isResizing, isOverResizeHandle, isResizeCooldown, onClick]);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if ('nativeEvent' in e) {
        e.nativeEvent.stopImmediatePropagation();
      }
      setIsResizing(true);
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
      setResizeStart({ x: clientX, y: clientY, width: videoSize.width, height: videoSize.height });
    },
    [videoSize]
  );

  const handleResizeMove = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!isResizing) return;
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const deltaX = clientX - resizeStart.x;
      const aspectRatio = 16 / 9;
      // Invert deltaX since resize handle is on the left side (moving left increases width)
      const newWidth = Math.max(160, Math.min(756, resizeStart.width - deltaX));
      const newHeight = Math.max(90, Math.min(450, newWidth / aspectRatio));
      setVideoSize({ width: newWidth, height: newHeight });
    },
    [isResizing, resizeStart]
  );

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
    setIsResizeCooldown(true);
    if (resizeCooldownTimeoutRef.current) {
      clearTimeout(resizeCooldownTimeoutRef.current);
    }
    resizeCooldownTimeoutRef.current = setTimeout(() => {
      setIsResizeCooldown(false);
      resizeCooldownTimeoutRef.current = null;
    }, 300);
  }, []);

  // Attach document-level listeners during resize
  useEffect(() => {
    if (isResizing) {
      const preventDefault = (e: TouchEvent): void => {
        e.preventDefault();
      };
      const handleTouchMove = (e: TouchEvent): void => {
        preventDefault(e);
        handleResizeMove(e);
      };

      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleResizeEnd);
      document.addEventListener('touchcancel', handleResizeEnd);

      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleResizeEnd);
        document.removeEventListener('touchcancel', handleResizeEnd);
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  // Cleanup cooldown timeout on unmount
  useEffect(() => {
    return () => {
      if (resizeCooldownTimeoutRef.current) {
        clearTimeout(resizeCooldownTimeoutRef.current);
      }
    };
  }, []);

  const isTop = position === 'top';
  const positionClasses = isTop ? 'top-0 right-0' : 'bottom-0 right-0';
  const reopenPositionClasses = isTop ? 'top-0 right-0' : 'bottom-[13%] sm:bottom-0 right-0';

  return (
    <>
      <Box
        className={`absolute ${positionClasses} overflow-hidden cursor-pointer`}
        style={{ zIndex: 99999 }}
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          bgcolor: 'background.level1',
          width: `${videoSize.width}px`,
          height: `${videoSize.height}px`,
          // An outline takes no room from the 16:9 content box, unlike a border.
          outline: `2px solid ${borderColor}`,
          outlineOffset: '-2px',
          opacity: isVisible ? 1 : 0,
          visibility: isVisible ? 'visible' : 'hidden',
          transition: 'opacity 0.3s ease-in-out, visibility 0.3s ease-in-out'
        }}
      >
        {/* Hide button — bottom-right */}
        <Box
          className='absolute bottom-0 right-0 z-50 cursor-pointer'
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            toggleVisibility();
          }}
          onMouseEnter={() => setIsHovered(true)}
          sx={{ opacity: isHovered ? 1 : 0, transition: 'opacity 0.2s ease-in-out' }}
        >
          <PiPHide className='w-8 h-8' style={{ transform: 'scaleX(-1)' }} />
        </Box>

        {/* Resize handle — top-left for bottom PiP, bottom-left for top PiP */}
        <Box
          className={`absolute ${isTop ? 'bottom-0 left-0' : 'top-0 left-0'} z-50`}
          onMouseDown={handleResizeStart}
          onTouchStart={handleResizeStart}
          onMouseEnter={(e) => {
            e.stopPropagation();
            setIsOverResizeHandle(true);
            setIsHovered(true);
          }}
          onMouseLeave={(e) => {
            e.stopPropagation();
            setIsOverResizeHandle(false);
          }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onTouchEnd={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          sx={{
            opacity: isHovered ? 1 : 0,
            transition: 'opacity 0.2s ease-in-out',
            '@media (hover: none) and (pointer: coarse)': {
              opacity: 1,
              minWidth: '44px',
              minHeight: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              touchAction: 'none'
            }
          }}
        >
          <PipResize className='w-8 h-8' style={{ transform: isTop ? 'rotate(180deg)' : 'rotate(270deg)' }} />
        </Box>

        {/* Content */}
        <Box
          className='relative flex justify-center items-center w-full h-full'
          sx={{ '& > div': { width: '100%', height: '100%' } }}
        >
          {children}
        </Box>
      </Box>

      {/* Reopen button when hidden */}
      {!isVisible && (
        <Box
          className={`absolute ${reopenPositionClasses} z-30 rounded-md cursor-pointer`}
          style={{ zIndex: 99999 }}
          onClick={toggleVisibility}
          sx={{ bgcolor: 'background.level1', opacity: 0.6, transition: 'opacity 0.3s ease-in-out' }}
        >
          <PiPButtonRight className='w-8 h-8' style={{ transform: 'scaleX(-1)' }} />
        </Box>
      )}
    </>
  );
};

export default React.memo(DualVideoPiP);
